import React, { useEffect, useState } from 'react';
import { Alert, Box, CircularProgress, Container, Divider, List, Typography } from '@mui/material';
import { WixLineItem } from '../../model/cartModel';
import { getProductColor, getProductPropertyByName } from '../../util/productUtils';
import { useBuwlCKOContext } from '../../context/GlobalBuwlCkoAppContext';
import { BuwlSourceSystem, Language } from '../../model/IBuwlCKOContext';
import { useTranslation } from 'react-i18next';
import OrderSummaryItems from './OrderSummaryItems';
import PriceSummary from './PriceSummary';
import { Payment } from '../../model/Payment';

interface OrderSummaryProps {
    wixItems: WixLineItem[];
    subtotal: string;
    shipping: string;
    tax: string;
    payment: Payment;
    isCartLoading: boolean;
}

const OrderSummary: React.FC<OrderSummaryProps> = ({ wixItems, subtotal, shipping, tax, payment, isCartLoading }) => {
    const { t, i18n } = useTranslation();
    const { buwlContext } = useBuwlCKOContext();
    const [isDiscountIncluded, setDiscountIncluded] = useState(true);
    const isDiscountRequired = false;

    useEffect(() => {
        if (buwlContext && buwlContext.language) {
            const languageKey = buwlContext.language;
            i18n.changeLanguage(Language[languageKey].toLowerCase());
        }
    }, [buwlContext, i18n]);

    const itemsToRender = wixItems.map((wixItem) => ({
        productName: wixItem.productName.translated,
        image: wixItem.image,
        price: wixItem.price.formattedAmount,
        fullPrice: wixItem.fullPrice.formattedAmount,
        quantity: wixItem.quantity,
        braSize: getProductPropertyByName(wixItem, 'Розмір ліфа'),
        pantiesSize: getProductPropertyByName(wixItem, 'Розмір трусиків'),
        beltSize: getProductPropertyByName(wixItem, 'Розмір пояса'),
        color: getProductColor(wixItem),
    }));

    const renderLoadingState = () => (
        <Typography sx={{ flexGrow: 1, textAlign: 'center', marginTop: '10px' }}>
            <CircularProgress color="inherit" size={30} />
        </Typography>
    );

    const renderEmptyState = () => (
        <Typography sx={{ flexGrow: 1, textAlign: 'center', marginTop: '10px' }}>{t('noCart')}</Typography>
    );

    return (
        <Container maxWidth="lg" className="SummaryComponent" sx={{ bgcolor: 'primary.main', p: 4 }}>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    '& .MuiTextField-root': { m: 1 },
                    '& .MuiButton-root': { m: 1 },
                }}
            >
                <Typography variant="h5" component="h2">
                    {t('orderSummaryInfo')}
                </Typography>
                <Typography>
                    <a
                        style={{ color: 'black' }}
                        href={
                            buwlContext.sourceSystem == BuwlSourceSystem.BUWL_WEB_UA
                                ? buwlContext.config.REACT_APP_WIX_CART_PAGE_URL
                                : buwlContext.config.REACT_APP_WIX_CART_PL_PAGE_URL
                        }
                    >
                        {t('changeOrder')}
                    </a>
                </Typography>
                <Divider sx={{ mt: 2 }} />
                {itemsToRender.length === 0 && isCartLoading && renderLoadingState()}
                {itemsToRender.length === 0 && !isCartLoading && renderEmptyState()}
                {itemsToRender.length !== 0 && (
                    <List disablePadding sx={{ mt: 2 }}>
                        {itemsToRender.map((item, index) => (
                            <OrderSummaryItems setDiscountIncluded={setDiscountIncluded} key={index} item={item} />
                        ))}
                        <Divider sx={{ mt: 2 }} component="li" />
                    </List>
                )}
                <PriceSummary
                    subtotal={subtotal}
                    shipping={shipping}
                    tax={tax}
                    payment={payment}
                    sourceSystem={buwlContext.sourceSystem}
                />
                {!isDiscountIncluded && isDiscountRequired && (
                    <Alert sx={{ mt: 2 }} severity="warning">
                        {t('discountIsNotLoaded')}{' '}
                        {buwlContext.sourceSystem == BuwlSourceSystem.BUWL_WEB_UA && (
                            <a style={{ color: 'black' }} href="https://www.instagram.com/blessed_undressed.ua/">
                                Instagram
                            </a>
                        )}
                        {buwlContext.sourceSystem == BuwlSourceSystem.BUWL_WEB_PL && (
                            <a style={{ color: 'black' }} href="https://www.instagram.com/blessed_undressed.pl/">
                                Instagram
                            </a>
                        )}
                    </Alert>
                )}
            </Box>
        </Container>
    );
};

export default OrderSummary;
